// ModalAssignServices.tsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Checkbox, 
  FormControlLabel, 
  Typography, 
  Box,
  CircularProgress,
} from '@mui/material';
import { 
  Assignment as AssignmentIcon, 
} from '@mui/icons-material';
import { useGetAllServices } from '../../../api/service';
import useStyles from './styles';

interface Service {
  id: number;
  name: string;
  description?: string;
}

interface ModalAssignServicesProps {
  open: boolean;
  selectedSignIds: number[];
  onAssign: (serviceIds: number[]) => void;
  onCancel: () => void;
  onClose: () => void;
}

export const ModalAssignServices: React.FC<ModalAssignServicesProps> = ({
  open,
  onAssign,
  onCancel,
  onClose
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [selectedServices, setSelectedServices] = useState<number[]>([]);
  
  // Fetch all available services
  const { services, isPending: isLoading } = useGetAllServices();

  // Reset selected services when modal opens
  useEffect(() => {
    if (open) {
      setSelectedServices([]);
    }
  }, [open]);

  const handleServiceToggle = (serviceId: number) => {
    setSelectedServices(prev => 
      prev.includes(serviceId) 
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const handleSelectAll = () => {
    if (services && services.length > 0) {
      if (selectedServices.length === services.length) {
        setSelectedServices([]);
      } else {
        setSelectedServices(services.map((service: Service) => service.id));
      }
    }
  };

  const handleAssign = () => {
    if (selectedServices.length > 0) {
      onAssign(selectedServices);
    }
  };

  const handleCancel = () => {
    setSelectedServices([]);
    onCancel();
  };

  // Type guard to ensure services is an array
  const servicesArray: Service[] = Array.isArray(services) ? services : [];
  const isAllSelected = selectedServices.length === servicesArray.length && servicesArray.length > 0;
  const isIndeterminate = selectedServices.length > 0 && selectedServices.length < servicesArray.length;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      classes={{
        paper: classes.dialogPaper
      }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Box display="flex" alignItems="center" gap={1}>
          <AssignmentIcon color="primary" />
          {t("sign.assignServicesTitle", "Assign Services to Signs")}
        </Box>
      </DialogTitle>
      
      <DialogContent className={classes.dialogContent}>
        {/* Select All Services */}
        <Box className={classes.selectAllContainer}>
          <FormControlLabel
            className={classes.selectAllLabel}
            control={
              <Checkbox
                className={classes.selectAllCheckbox}
                checked={isAllSelected}
                indeterminate={isIndeterminate}
                onChange={handleSelectAll}
                disabled={isLoading || servicesArray.length === 0}
              />
            }
            label={t("sign.selectAllServices", "Select All Services")}
          />
        </Box>

        {/* Services List */}
        <Box className={classes.servicesContainer}>
          {isLoading ? (
            <Box className={classes.loadingContainer}>
              <CircularProgress size={24} />
              <Typography className={classes.loadingText} sx={{ ml: 2 }}>
                {t("common.loading", "Loading...")}
              </Typography>
            </Box>
          ) : servicesArray.length === 0 ? (
            <Box className={classes.emptyStateContainer}>
              <AssignmentIcon className={classes.emptyStateIcon} />
              <Typography className={classes.emptyStateText}>
                {t("sign.noServicesAvailable", "No services available")}
              </Typography>
            </Box>
          ) : (
            <Box className={classes.servicesList}>
              {servicesArray.map((service: Service) => {
                const isSelected = selectedServices.includes(service.id);
                return (
                  <FormControlLabel
                    key={service.id}
                    className={`${classes.serviceItem} ${isSelected ? classes.serviceItemSelected : ''}`}
                    control={
                      <Checkbox
                        className={classes.checkbox}
                        checked={isSelected}
                        onChange={() => handleServiceToggle(service.id)}
                      />
                    }
                    label={
                      <Box className={classes.serviceContent}>
                        <Typography className={classes.serviceName}>
                          {service.name}
                        </Typography>
                        {service.description && (
                          <Typography className={classes.serviceDescription}>
                            {service.description}
                          </Typography>
                        )}
                        <Typography className={classes.serviceCode}>
                          ID: {service.id}
                        </Typography>
                      </Box>
                    }
                  />
                );
              })}
            </Box>
          )}
        </Box>

      </DialogContent>
      
      <DialogActions className={classes.dialogActions}>
        <Button 
          onClick={handleCancel} 
          className={classes.cancelButton}
          variant="outlined"
        >
          {t("common.cancel", "Cancel")}
        </Button>
        <Button 
          onClick={handleAssign} 
          className={classes.assignButton}
          disabled={selectedServices.length === 0}
          variant="contained"
          startIcon={<AssignmentIcon />}
        >
          {t("sign.assignServices", "Assign Services")} 
          {selectedServices.length > 0 && `(${selectedServices.length})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};